import { Backspace } from './extensions/backspace'
import Bold from '@tiptap/extension-bold'
import Blockquote from '@tiptap/extension-blockquote'
import BubbleMenu from '@tiptap/extension-bubble-menu'
import BulletList from '@tiptap/extension-bullet-list'
import Code from '@tiptap/extension-code'
import CodeBlock from '@tiptap/extension-code-block'
import Color from '@tiptap/extension-color'
import Document from '@tiptap/extension-document'
import Dropcursor from '@tiptap/extension-dropcursor'
import { ExtendedHardBreak } from './extensions/hard_break'
import { ExtendedMention } from './extensions/mention'
import { FaIcon } from './extensions/fa_icon'
import { FaListItem } from './extensions/fa_list_item'
import Focus from '@tiptap/extension-focus'
import { FormSubmit } from './extensions/form_submit'
import Gapcursor from '@tiptap/extension-gapcursor'
import HardBreak from '@tiptap/extension-hard-break'
import Heading from '@tiptap/extension-heading'
import Highlight from '@tiptap/extension-highlight'
import History from '@tiptap/extension-history'
import HorizontalRule from '@tiptap/extension-horizontal-rule'
import { ExtendedImage } from './extensions/image'
import { Indent } from './extensions/indent'
import Italic from '@tiptap/extension-italic'
import { LinkInfoBox } from './extensions/link_info_box'
import ListKeymap from '@tiptap/extension-list-keymap'
import ListItem from '@tiptap/extension-list-item'
import OrderedList from '@tiptap/extension-ordered-list'
import Paragraph from '@tiptap/extension-paragraph'
import Placeholder from '@tiptap/extension-placeholder'
import { extendLinkExtension } from './extensions/simplero_link'
import Strike from '@tiptap/extension-strike'
import Table from '@tiptap/extension-table'
import TableCell from '@tiptap/extension-table-cell'
import TableHeader from '@tiptap/extension-table-header'
import TableRow from '@tiptap/extension-table-row'
import Text from '@tiptap/extension-text'
import TextAlign from '@tiptap/extension-text-align'
import { TextStyle, FontFamily, FontSize } from '@tiptap/extension-text-style'
import Underline from '@tiptap/extension-underline'
import { BotObject } from './extensions/bot_object'
import botObjectSuggestion from './extensions/bot_object_suggestion'
import { ChatBotSkill } from './extensions/chat_bot_skill'
import { Decoration } from './extensions/decoration'
import { Interpolation } from './extensions/interpolation'
import { InterpolationSuggestion } from './extensions/interpolation_suggestion'
import {
  resolveTippyPortal,
  guardTippySetProps,
  guardSelectionDrags,
  fontFamilyAttribute,
  TOOLBAR_TIPPY_CLASS,
} from './utilities'

let _stickyTopBarHeight = null
function stickyTopBarHeight() {
  if (_stickyTopBarHeight === null) {
    _stickyTopBarHeight =
      (parseInt(
        getComputedStyle(document.body).getPropertyValue(
          '--sticky-top-bar-height'
        )
      ) || 0) + 8
  }
  return _stickyTopBarHeight
}

// Reject autolink when {{ appears before or }} appears after the candidate URL.
// Only checks the text node at the cursor position for performance.
function isInsideInterpolation(url, editorController) {
  if (!editorController.editor) return false

  const { state } = editorController.editor
  const pos = state.selection.from
  const resolved = state.doc.resolve(pos)
  const node = resolved.parent

  if (!node.isTextblock) return false

  const text = node.textContent
  const idx = text.indexOf(url)
  if (idx === -1) return false

  const before = text.substring(0, idx).replace(/ +$/, '')
  const after = text.substring(idx + url.length).replace(/^ +/, '')
  return before.endsWith('{{') || after.startsWith('}}')
}

export function configuredExtensions(editorController) {
  // Before any toolbar exists: the floating overlays must already be out of hit-testing for
  // the user's first drag, not just for the second one.
  guardSelectionDrags(editorController.element?.ownerDocument)

  const extensions = [
    Backspace,
    Bold,
    Blockquote.extend({
      content: '(block | heading)+',
    }),
    BulletList.configure({
      keepMarks: true,
      keepAttributes: true,
      HTMLAttributes: {
        class: editorController.forBulletListValue ? 'fa-bullet-list' : null,
      },
    }),
    Code,
    CodeBlock,
    Color.configure({
      types: ['textStyle'],
    }),
    doumentNode(editorController),
    Decoration,
    Dropcursor,
    Interpolation,
    Focus.configure({
      mode: 'deepest',
    }),
    FontFamily.extend({
      addGlobalAttributes() {
        return [
          {
            types: this.options.types,
            attributes: {
              fontFamily: fontFamilyAttribute,
            },
          },
        ]
      },
    }),
    FontSize.extend({
      addGlobalAttributes() {
        // Add line-height: normal !important to break the typography preset's !important constraint
        return [
          {
            types: this.options.types,
            attributes: {
              fontSize: {
                default: null,
                parseHTML: (element) => element.style.fontSize,
                renderHTML: (attributes) => {
                  if (!attributes.fontSize) {
                    return {}
                  }

                  return {
                    style: `font-size: ${attributes.fontSize}; line-height: normal;`,
                  }
                },
              },
            },
          },
        ]
      },
    }),
    Gapcursor,
    editorController.formSubmitShortcutValue ? ExtendedHardBreak : HardBreak,
    extendedHeadingNode(editorController),
    Highlight.configure({
      multicolor: true,
      parseHTML() {
        return [
          {
            tag: 'span',
          },
        ]
      },
    }),
    History,
    HorizontalRule,
    indent(editorController),
    Italic,
    listItem(editorController),
    OrderedList,
    Paragraph,
    extendLinkExtension(editorController).configure({
      openOnClick: false,
      // Pasting a URL over a selection hyperlinks the selection (see
      // isBareUrlOverSelection in utilities.js) — pinned explicitly rather than
      // left to the dependency's own default, since editor_component_controller.js
      // depends on this staying true.
      linkOnPaste: true,
      validate: (url) => !isInsideInterpolation(url, editorController),
    }),
    Placeholder.configure({
      placeholder: editorController.placeholderValue,
    }),
    Strike,
    Table.configure({
      resizable: true,
    }),
    TableCell,
    TableHeader,
    TableRow,
    Text,
    TextAlign.configure({
      types: ['heading', 'paragraph'],
    }),
    TextStyle,
    Underline,
  ]

  if (editorController.formSubmitShortcutValue) {
    extensions.push(FormSubmit)
  }

  if (editorController.hasToolbarTarget) {
    extensions.push(
      BubbleMenu.configure({
        element: editorController.toolbarTarget,
        tippyOptions: {
          detachOnUnmount: false,
          maxWidth: 'none',
          hideOnClick: true,
          appendTo: resolveTippyPortal,
          onCreate(instance) {
            // TOOLBAR_TIPPY_CLASS is how CSS reaches this root without a `:has()`.
            instance.popper.classList.add('tw', TOOLBAR_TIPPY_CLASS)
            // BubbleMenu calls setProps on every editor transaction. Guard
            // tippy's nested-popper forceUpdate so a leftover hidden child
            // (color picker, tooltip) cannot abort formatting.
            guardTippySetProps(instance)
          },
          onShow(instance) {
            instance.popper.style.maxHeight = ''
            instance.popper.style.maxWidth = ''
          },
          onShown(_instance) {
            editorController.syncToolbarState()
            // toolbarEl (cached pre-detach), not toolbarTarget — appendTo now moves the toolbar
            // into .tippy-portal, outside this controller's element, so the live Stimulus target
            // lookup can no longer find it.
            editorController.toolbarEl.style.visibility = 'visible'
            editorController.bindPortaledToolbarActions()
          },
          onHidden(instance) {
            // We don't remove the tippy element from DOM and it is hidden with visibility: hidden which takes up space on the DOM
            // So setting max-height: 0 removes that whitespace
            // As tippy uses transitions - display: none causes issues and cannot be used
            instance.popper.style.maxHeight = 0
            instance.popper.style.maxWidth = 0
            editorController.toolbarEl.style.visibility = 'hidden'
          },
          popperOptions: {
            strategy: 'fixed',
            modifiers: [
              {
                name: 'flip',
                options: {
                  padding: {
                    top: stickyTopBarHeight(),
                    bottom: 8,
                    left: 8,
                    right: 8,
                  },
                },
              },
              {
                // BubbleMenu anchors the toolbar to the whole selection and places it above
                // that. Drag a selection downwards far enough for the page to scroll and the
                // top of it — the anchor — ends up above the viewport, taking the toolbar
                // with it: on release you have a selection you can see and a toolbar you
                // can't. Keep it on screen rather than following the anchor out of view.
                // Only engages when it would otherwise overflow, so ordinary selections are
                // placed exactly as before.
                //
                // `tether` stays on (popper's default) deliberately. Turning it off does keep
                // the toolbar onscreen, but it keeps it there even once the whole selection
                // has scrolled away — a toolbar pinned to the viewport with nothing selected
                // in view. Tethered, it still clamps while any of the selection is visible
                // and leaves with the selection when none of it is.
                //
                // `altBoundary` because the toolbar is portaled to the end of <body>: without
                // it popper measures the popper's own clipping parents, which by then are
                // none, leaving only the viewport. Measuring the editor's instead keeps the
                // toolbar inside a scrollable side panel or modal rather than the window.
                name: 'preventOverflow',
                options: {
                  altAxis: true,
                  tether: true,
                  altBoundary: true,
                  padding: { top: stickyTopBarHeight(), bottom: 8 },
                },
              },
            ],
          },
        },
      })
    )
  }

  if (editorController.mentionsUrlValue) {
    extensions.push(
      ExtendedMention.configure({ url: editorController.mentionsUrlValue })
    )
  }

  if (editorController.withImageValue) {
    extensions.push(
      ExtendedImage.configure({
        // Inline, like img actually is in HTML: as a block node, ProseMirror silently drops any
        // image it finds inside a <p> — which is where marked puts every markdown image, and
        // where most pasted HTML has them.
        inline: true,
      })
    )
  }

  if (editorController.forBulletListValue) {
    extensions.push(ListKeymap)
  }

  if (editorController.linkPickerValue) {
    extensions.push(LinkInfoBox)
  } else if (editorController.hasToolbarTarget) {
    // The site toolbar's link form instead of the admin link dialog.
    extensions.push(
      LinkInfoBox.configure({
        onEdit: () => editorController.editLink(),
        labels: editorController.linkBoxLabelsValue,
      })
    )
  }

  if (editorController.personalizationsValue?.length) {
    extensions.push(
      InterpolationSuggestion.configure({
        personalizations: editorController.personalizationsValue,
        interpolationStyle:
          editorController.interpolationStyleValue || 'liquid',
      })
    )
  }

  if (editorController.chatBotSkillsValue?.length) {
    extensions.push(
      ChatBotSkill.configure({ skills: editorController.chatBotSkillsValue })
    )
  }

  if (editorController.withIconInsertValue) {
    extensions.push(FaIcon)
  }

  if (editorController.botObjectsValue) {
    extensions.push(
      BotObject.configure({
        pickerUrl: editorController.botObjectPickerUrlValue,
        suggestion: botObjectSuggestion,
      })
    )
  }

  return extensions
}

// The fa-bullet-list editor uses Tab / Shift-Tab for nesting list items (handled
// in FaListItem). We strip Indent's keyboard shortcuts so they don't grab Tab
// before FaListItem's nesting handler — but keep the extension loaded so the
// `indent` global attribute still parses and re-emits `style="padding-left: …"`
// on legacy items that used Tab-based padding before nesting existed.
function indent(editorController) {
  if (editorController.forBulletListValue) {
    return Indent.extend({
      addKeyboardShortcuts() {
        return {}
      },
    })
  }
  return Indent
}

function listItem(editorController) {
  if (editorController.forBulletListValue) {
    if (editorController.listIconValue) {
      return FaListItem.configure({
        HTMLAttributes: { iconClass: editorController.listIconValue },
      })
    } else {
      return FaListItem
    }
  } else {
    return ListItem
  }
}

// Extend the heading node to assign it to a different group than "block".
// This ensures that the documentNode function allows only one heading element.
// If heading remains in the "block" group, multiple headings would be allowed because "block*" permits it.
function extendedHeadingNode(editorController) {
  if (editorController.forHeadingValue) {
    return Heading.extend({
      group: 'heading',
    })
  } else {
    return Heading
  }
}

function doumentNode(editorController) {
  let contentType

  if (editorController.forHeadingValue) {
    // We want to allow only one heading tag on the headline elements on builder
    contentType = '(heading | blockquote) block*'
  } else if (editorController.forBulletListValue) {
    contentType = 'bulletList'
  }

  if (contentType) {
    return Document.extend({
      content: contentType,
    })
  } else {
    return Document
  }
}
